/**
 * Nav collection grid — client-side filters (v2)
 * Filters instantly from DOM product data; enriches from products.json in background.
 */
(function () {
  'use strict';

  const MOBILE_MAX_WIDTH = 999;
  const APPLY_DEBOUNCE_MS = 16;
  const FACET_SYNC_DEBOUNCE_MS = 48;
  const TARGETED_HANDLE_FETCH_MAX = 160;
  const CATALOG_BATCH = 250;
  const FETCH_HANDLE_BATCH = 80;
  const FETCH_CONCURRENCY = 6;
  const FILTER_PARAM_KEYS = ['availability', 'vendor', 'product_type', 'size', 'color', 'tag', 'price_min', 'price_max', 'sort_by'];
  const EDITOR_QUERY_KEYS = ['preview_theme_id', 'oseid', 'key', 'v', '_fd', 'pb'];
  const IGNORED_URL_FILTER_KEYS = new Set([
    'sort_by',
    'page',
    'sections',
    'section_id',
    'preview_theme_id',
    'oseid',
    'key',
    'v',
    '_fd',
    'pb',
    'nav_col_render',
    'nav_col_render_all',
    'nav_col_catalog',
    'filtered_handles',
    'nav_col_filter_debug',
    'q',
    'type',
    'options[prefix]'
  ]);

  function handleize(str) {
    return String(str || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function expandTagFilterValue(value) {
    return String(value || '')
      .split('|')
      .map((part) => handleize(part))
      .filter(Boolean);
  }

  function resolveTagFilterHandles(selectedValues, group) {
    const handles = new Set();

    Array.from(selectedValues || []).forEach((rawValue) => {
      expandTagFilterValue(rawValue).forEach((handle) => handles.add(handle));
    });

    if (!group) return [...handles];

    const labels = Array.isArray(group.tags) ? group.tags : [];
    const configuredHandles = Array.isArray(group.tagHandles) ? group.tagHandles : [];

    configuredHandles.forEach((configuredHandle, index) => {
      const configured = handleize(configuredHandle);
      const labelHandle = handleize(labels[index] || '');
      const optionHandles = new Set([configured, labelHandle].filter(Boolean));
      const selected = expandTagFilterValue(
        [...selectedValues].find((value) => {
          const wanted = expandTagFilterValue(value);
          return wanted.some((part) => optionHandles.has(part));
        }) || ''
      );

      if (
        selected.some((part) => optionHandles.has(part)) ||
        [...handles].some((part) => optionHandles.has(part))
      ) {
        optionHandles.forEach((handle) => handles.add(handle));
        expandTagFilterValue(labels[index] || '').forEach((handle) => handles.add(handle));
        expandTagFilterValue(configuredHandle || '').forEach((handle) => handles.add(handle));
      }
    });

    return [...handles];
  }

  function normalizeTagPrefix(prefix) {
    const raw = String(prefix || '').trim();
    if (!raw) return '';
    const handle = handleize(raw);
    if (!handle) return '';
    return handle.endsWith('-') ? handle : handle + '-';
  }

  function productHasTagHandle(productTags, wantedHandle, tagPrefix) {
    const wanted = handleize(wantedHandle);
    if (!wanted) return false;
    const dynamicPrefix = normalizeTagPrefix(tagPrefix);

    return (productTags || []).some((productTag) => {
      const tag = handleize(productTag);
      if (!tag) return false;
      if (tag === wanted) return true;
      if (dynamicPrefix && tag === dynamicPrefix + wanted) return true;
      if (tag === 'category-' + wanted) return true;
      if (tag === 'gender-' + wanted) return true;
      if (tag === 'product-fits-' + wanted) return true;
      if (tag === 'product-type-' + wanted) return true;
      return false;
    });
  }

  function productMatchesTagHandles(product, wantedHandles, tagPrefix) {
    const handles = (wantedHandles || []).map(handleize).filter(Boolean);
    if (!handles.length) return false;
    return handles.some((wanted) => productHasTagHandle(product.tags, wanted, tagPrefix));
  }

  function normalizeColorKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/colour/g, 'color')
      .replace(/&/g, ' and ')
      .replace(/\bcolors?\b/g, '')
      .replace(/\band\b/g, ' ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .trim();
  }

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(String(value || ''));
    }
    return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function formatLabel(handle) {
    return String(handle || '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  function formatSizeLabel(handle) {
    const raw = String(handle || '').trim();
    if (!raw) return '';
    if (/^\d+$/.test(raw)) return raw;
    return raw.replace(/-/g, ' ').toLowerCase();
  }

  const SIZE_LETTER_RANK = {
    'xxs': 100,
    'xx-small': 105,
    'xxsmall': 105,
    'xs': 110,
    'x-small': 115,
    'xsmall': 115,
    'extra-small': 115,
    's': 120,
    'small': 125,
    'm': 130,
    'med': 132,
    'medium': 135,
    'l': 140,
    'large': 145,
    'xl': 150,
    'x-large': 155,
    'xlarge': 155,
    'extra-large': 155,
    'xxl': 160,
    '2xl': 165,
    'xxxl': 170,
    '3xl': 175,
    '4xl': 180,
    'xxxxl': 180,
    '5xl': 185,
    '6xl': 190,
    '7xl': 195,
    '8xl': 200,
    'one-size': 50,
    'onesize': 50,
    'os': 55,
    'o-s': 55
  };

  function normalizeSizeSortToken(value) {
    return handleize(String(value || '').trim()).replace(/^size-/, '');
  }

  function getSizeSortKey(value) {
    const token = normalizeSizeSortToken(value);
    if (!token) return [9999, 0, ''];

    if (/^\d+$/.test(token)) {
      return [500, parseInt(token, 10), token];
    }

    const numericXl = token.match(/^(\d+)xl$/);
    if (numericXl) {
      const n = parseInt(numericXl[1], 10);
      return [150 + n * 5, n, token];
    }

    const numericXxl = token.match(/^(\d+)xxl$/);
    if (numericXxl) {
      const n = parseInt(numericXxl[1], 10);
      return [160 + n * 5, n, token];
    }

    if (SIZE_LETTER_RANK[token] != null) {
      return [SIZE_LETTER_RANK[token], 0, token];
    }

    const prefixed = token.match(/^(?:us|uk|eu|au)-(.+)$/);
    if (prefixed) {
      return getSizeSortKey(prefixed[1]);
    }

    const leadingNumber = token.match(/^(\d+)(?:-\d+)?/);
    if (leadingNumber && token.length <= 8) {
      return [500, parseInt(leadingNumber[1], 10), token];
    }

    return [9000, 0, token];
  }

  function compareSizeOrder(a, b) {
    const ka = getSizeSortKey(a);
    const kb = getSizeSortKey(b);
    if (ka[0] !== kb[0]) return ka[0] - kb[0];
    if (ka[1] !== kb[1]) return ka[1] - kb[1];
    return ka[2].localeCompare(kb[2], undefined, { numeric: true, sensitivity: 'base' });
  }

  function isMobile() {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
  }

  function stripCardHtml(html) {
    if (!html) return '';

    const cleaned = String(html)
      .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '')
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/>\s+</g, '><')
      .replace(/\s{2,}/g, ' ')
      .trim();

    try {
      const doc = new DOMParser().parseFromString(cleaned, 'text/html');
      const items = doc.querySelectorAll('li[data-product-handle]');
      if (items.length) {
        return [...items].map((li) => li.outerHTML).join('');
      }
    } catch (error) {
      console.warn('[NavColFilters] stripCardHtml parse failed', error);
    }

    return '';
  }

  function normalizeCatalogPayload(raw) {
    if (!raw || typeof raw !== 'object') return { page: 1, pages: 1, products: [] };
    return {
      page: parseInt(raw.p ?? raw.page, 10) || 1,
      pages: parseInt(raw.n ?? raw.pages, 10) || 1,
      products: Array.isArray(raw.d) ? raw.d : Array.isArray(raw.products) ? raw.products : []
    };
  }

  function minifyCachedCardHtml(html) {
    return String(html || '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/>\s+</g, '><')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  function reorderGridToHandles(grid, orderedHandles, gridItems) {
    if (!grid || !orderedHandles || !orderedHandles.length) return;
    const frag = document.createDocumentFragment();
    orderedHandles.forEach((handle) => {
      const li = gridItems && gridItems.get(handle)
        ? gridItems.get(handle)
        : grid.querySelector(`li[data-product-handle="${escapeSelector(handle)}"]`);
      if (li) frag.appendChild(li);
    });
    grid.appendChild(frag);
  }

  async function runPool(items, limit, worker) {
    if (!items.length) return [];
    const results = new Array(items.length);
    let index = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (index < items.length) {
        const current = index;
        index += 1;
        results[current] = await worker(items[current], current);
      }
    });
    await Promise.all(runners);
    return results;
  }

  function appendProductCards(grid, html, gridItems) {
    if (!grid || !html) return 0;

    const temp = document.createElement('div');
    temp.innerHTML = html;
    let appended = 0;

    temp.querySelectorAll('li[data-product-handle]').forEach((li) => {
      const handle = li.getAttribute('data-product-handle');
      if (!handle) return;
      if (gridItems && gridItems.has(handle)) return;
      if (!gridItems && grid.querySelector(`li[data-product-handle="${escapeSelector(handle)}"]`)) return;
      grid.appendChild(li);
      if (gridItems) gridItems.set(handle, li);
      appended += 1;
    });

    return appended;
  }

  function isThemeEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function mergeEditorSearchParams(params) {
    if (!isThemeEditor()) return params;
    const current = new URLSearchParams(window.location.search);
    EDITOR_QUERY_KEYS.forEach((key) => {
      const value = current.get(key);
      if (value) params.set(key, value);
    });
    return params;
  }

  function mergeSearchContextParams(params, config) {
    if (!config || !config.searchMode) return params;
    if (config.searchTerms) params.set('q', config.searchTerms);
    params.set('type', config.searchQueryType || 'product');
    params.set('options[prefix]', config.searchQueryPrefix || 'last');
    return params;
  }

  function collectFilterParamKeys(config, params) {
    const keys = new Set(FILTER_PARAM_KEYS);
    ((config && config.tagFilterGroups) || []).forEach((group) => {
      keys.add('tag_' + group.id);
    });
    if (params) {
      params.forEach((_, key) => {
        if (key.startsWith('tag_')) keys.add(key);
      });
    }
    return keys;
  }

  function urlHasActiveFilterParams(params, config) {
    const search = params || new URLSearchParams(window.location.search);
    const keys = collectFilterParamKeys(config, search);
    for (const key of search.keys()) {
      if (IGNORED_URL_FILTER_KEYS.has(key)) continue;
      if (!keys.has(key) && !key.startsWith('tag_')) continue;
      const value = search.get(key);
      if (value && String(value).trim()) return true;
    }
    return false;
  }

  function parseActiveFiltersFromUrlParams(params, config) {
    const active = new Map();
    const keys = collectFilterParamKeys(config, params);

    keys.forEach((key) => {
      if (key === 'price_min' || key === 'price_max' || key === 'sort_by') return;
      const raw = params.get(key);
      if (!raw) return;
      const values = String(raw)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      if (values.length) active.set(key, new Set(values));
    });

    const min = params.get('price_min');
    const max = params.get('price_max');
    const minValue = min ? parseFloat(min) || 0 : 0;
    const maxValue = max ? parseFloat(max) || Infinity : Infinity;
    if (minValue > 0 || maxValue < Infinity) {
      active.set('price', { min: minValue, max: maxValue });
    }

    return active;
  }

  class NavColFilters {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.section = document.querySelector('.nav-col-' + sectionId + '-grid');
      this.filtersEl = document.getElementById('custom-filters-' + sectionId);
      this.grid = this.section ? this.section.querySelector('#product-grid') : null;
      this.tagsEl = document.getElementById('active-filters-tags-' + sectionId);
      this.resultsEl = document.getElementById('filter-results-count');
      this.noResultsEl = document.getElementById('no-results-message-' + sectionId);
      this.config = null;
      this.products = [];
      this.byHandle = new Map();
      this.active = new Map();
      this.fullCatalogLoaded = false;
      this.catalogPromise = null;
      this.initialGridHandles = new Set();
      this.orderedHandles = [];
      this.visibleCount = 0;
      this.scrollLoading = false;
      this.scrollObserver = null;
      this._scrollLoadHandler = null;
      this.cardHtmlCache = new Map();
      this.cachedCardPages = new Set();
      this.fullCardCachePromise = null;
      this.fullCardCacheWarmed = false;
      this.applyTimer = null;
      this.facetSyncTimer = null;
      this.filtered = false;
      this._optionsBuilt = false;
      this._filtersReady = false;
      this._urlLoaded = false;
      this._urlFiltersPending = false;
      this._applying = false;
      this._cleanups = [];
      this._suppress = false;
      this._facetPoolCache = new Map();
      this.gridItems = new Map();
      this._matchFn = null;
      this.debug = window.NavColFilterDebug
        ? window.NavColFilterDebug.forSection(sectionId)
        : { log: function () {}, warn: function () {}, error: function () {}, time: function (_l, fn) { return typeof fn === 'function' ? fn() : undefined; }, snapshot: function () {}, audit: function () {}, attach: function () {} };

      if (!this.section || !this.filtersEl || !this.grid || this.grid.tagName !== 'UL') {
        this.debug.error('init-abort', {
          section: !!this.section,
          filtersEl: !!this.filtersEl,
          grid: !!this.grid,
          gridTag: this.grid ? this.grid.tagName : null
        });
        return;
      }

      const configEl = document.getElementById('NavColGridConfig-' + sectionId);
      if (configEl) {
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (error) {
          console.warn('[NavColFilters] config parse failed', error);
          this.debug.error('config-parse-failed', { message: error.message });
        }
      }

      this.debug.log('init-start', { sectionId: sectionId });
      this.indexFromDom();
      this.bindEvents();
      this.debug.attach(this);
      this.debug.audit(this.section);

      if (!isThemeEditor() && urlHasActiveFilterParams(null, this.config)) {
        this._urlFiltersPending = true;
        this.prepareGridForUrlFilters();
      }

      if (this.config && this.needsFullCatalog()) {
        this.filtersEl.classList.add('catalog-loading');
        this.filtersEl.classList.remove('catalog-synced');
        this.onFiltersReady({ skipCatalog: true });
        this.scheduleFullCatalogLoad();
      } else {
        this.fullCatalogLoaded = true;
        this.onFiltersReady();
      }
    }

    scheduleFullCatalogLoad() {
      if (this._catalogScheduled) return;

      if (isMobile()) {
        this.bindMobileCatalogPrime();
        return;
      }

      this._catalogScheduled = true;
      void this.ensureFullCatalogLoad();
    }

    bindMobileCatalogPrime() {
      if (this._mobileCatalogBound) return;
      this._mobileCatalogBound = true;

      const prime = () => {
        void this.ensureFullCatalogLoad();
      };

      const section = this.section;
      section?.querySelector('.mobile-filters-tab')?.addEventListener('click', prime);
      section?.querySelector('.desktop-filters-tab')?.addEventListener('click', prime);

      const onPrime = (event) => {
        if (!event?.detail?.sectionId || event.detail.sectionId === this.config?.sectionApiId) {
          prime();
        }
      };
      window.addEventListener('nav-col:prime-catalog', onPrime);
      this.track(() => window.removeEventListener('nav-col:prime-catalog', onPrime));

      if (!this.needsFullCatalog()) return;

      let scrollPrimed = false;
      const onScroll = () => {
        if (scrollPrimed || this.fullCatalogLoaded) return;
        const remaining =
          document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
        if (remaining > 900) return;
        scrollPrimed = true;
        prime();
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      this.track(() => window.removeEventListener('scroll', onScroll));
    }

    ensureFullCatalogLoad() {
      if (this.fullCatalogLoaded) return Promise.resolve(this.products);
      if (!this.catalogPromise) {
        this._catalogScheduled = true;
        this.catalogPromise = this.loadFullCatalog().finally(() => this.onCatalogReady());
      }
      return this.catalogPromise;
    }

    onCatalogReady() {
      this.fullCatalogLoaded = true;
      if (!this._optionsBuilt) {
        this.syncFilterOptions();
      } else {
        this.syncAllFacetCounts();
      }
      this.filtersEl.classList.remove('catalog-loading');
      this.filtersEl.classList.add('catalog-synced');
      this.clearFacetPoolCache();
      this.pushDebugSnapshot({ catalog: 'ready' });

      if (this._urlFiltersPending) {
        void this.tryApplyUrlFilters();
      } else if (!this.filtered && this.active.size === 0) {
        void this.restore();
      }
    }

    prepareGridForUrlFilters() {
      if (!this.grid) return;
      this.grid.classList.add('nav-col-filter-loading', 'nav-col-url-filter-pending');
    }

    clearUrlFilterPendingState() {
      this._urlFiltersPending = false;
      if (!this.grid) return;
      this.grid.classList.remove('nav-col-url-filter-pending', 'nav-col-filter-loading');
    }

    applyActiveFiltersToDom() {
      if (!this.filtersEl) return;
      this._suppress = true;

      this.filtersEl.querySelectorAll('.filter-checkbox').forEach((input) => {
        input.checked = false;
      });
      this.filtersEl.querySelectorAll('.price-input').forEach((input) => {
        input.value = '';
      });

      this.active.forEach((values, key) => {
        if (key === 'price') {
          const minEl = this.filtersEl.querySelector('input[name="price_min"]');
          const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
          if (minEl && values.min > 0) minEl.value = values.min;
          if (maxEl && values.max < Infinity) maxEl.value = values.max;
          return;
        }

        const selected = values instanceof Set ? values : new Set();
        selected.forEach((value) => {
          const input = this.filtersEl.querySelector(
            `input.filter-checkbox[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`
          );
          if (input) input.checked = true;
        });
      });

      this._suppress = false;
      this.syncTagBoxState();
    }

    async tryApplyUrlFilters() {
      if (!this._urlFiltersPending || isThemeEditor()) return;

      const params = new URLSearchParams(window.location.search);
      const fromUrl = parseActiveFiltersFromUrlParams(params, this.config);
      if (!fromUrl.size) {
        this.clearUrlFilterPendingState();
        return;
      }

      this.active = fromUrl;
      this.applyActiveFiltersToDom();

      if (!this.fullCatalogLoaded && this.needsFullCatalog()) {
        await this.ensureFullCatalogLoad();
      }

      if (this.active.size === 0) {
        this.clearUrlFilterPendingState();
        return;
      }

      await this.apply({ resetPage: false });
    }

    onFiltersReady({ skipCatalog = false } = {}) {
      if (this._filtersReady) return;
      this._filtersReady = true;

      if (!this._optionsBuilt) {
        if (!skipCatalog) {
          this.syncFilterOptions();
        }
      } else if (!skipCatalog) {
        this.syncAllFacetCounts();
      }

      if (!skipCatalog) {
        this.filtersEl.classList.remove('catalog-loading');
        this.filtersEl.classList.add('catalog-synced');
      }

      if (!this.filtered && this.active.size === 0 && !skipCatalog && !this._urlFiltersPending) {
        void this.restore();
      }

      this.pushDebugSnapshot();

      if (!this._urlLoaded) {
        this._urlLoaded = true;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.loadFromUrl());
        });
      } else if (this._urlFiltersPending) {
        void this.tryApplyUrlFilters();
      }
    }

    destroy() {
      window.clearTimeout(this.applyTimer);
      window.clearTimeout(this.facetSyncTimer);
      this.applyTimer = null;
      this.facetSyncTimer = null;
      this.destroyScrollObserver();
      while (this._cleanups.length) {
        const fn = this._cleanups.pop();
        try {
          fn();
        } catch (error) {
          /* ignore */
        }
      }
    }

    track(fn) {
      if (typeof fn === 'function') this._cleanups.push(fn);
    }

    log(event, detail) {
      if (this.debug && this.debug.log) this.debug.log(event, detail);
      if (!this.config || !this.config.filterDebug) return;
      console.info('[NavColFilters]', event, detail || '');
    }

    pushDebugSnapshot(extra) {
      if (!this.debug || !this.debug.snapshot) return;
      const visible = this.grid
        ? [...this.grid.querySelectorAll('li[data-product-handle]')].filter(
            (li) => !li.classList.contains('nav-col-filter-hidden')
          ).length
        : 0;
      const activeSummary = [...this.active.entries()]
        .map(([k, v]) => {
          if (v instanceof Set) return k + '=' + [...v].join('|');
          if (k === 'price') return 'price=' + v.min + '-' + v.max;
          return k;
        })
        .join(', ');
      this.debug.snapshot(
        Object.assign(
          {
            gridItems: this.grid ? this.grid.querySelectorAll('li[data-product-handle]').length : 0,
            indexed: this.products.length,
            visible: visible,
            activeSummary: activeSummary || 'none',
            filtered: this.filtered
          },
          extra || {}
        )
      );
    }

    getExpectedCount() {
      const n = parseInt(this.config && this.config.collectionProductCount, 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }

    shouldHideSoldOut() {
      return !(this.config && this.config.hideSoldOutProducts === false);
    }

    isSellableProduct(product) {
      return !this.shouldHideSoldOut() || product.availability !== 'out-of-stock';
    }

    needsFullCatalog() {
      const expected = this.getExpectedCount();
      if (!expected) return true;
      return this.products.length < expected;
    }

    /* ——— Product index ——— */

    addProduct(product) {
      if (!product || !product.handle) return;
      if (!this.isSellableProduct(product)) return;
      const existing = this.byHandle.get(product.handle);
      if (existing) {
        Object.assign(existing, product);
        return;
      }
      this.byHandle.set(product.handle, product);
      this.products.push(product);
    }

    parseCardElement(card) {
      const price = parseFloat(String(card.dataset.price || '').replace(/,/g, '')) || 0;
      return {
        vendor: handleize(card.dataset.vendor || ''),
        type: handleize(card.dataset.productType || ''),
        tags: (card.dataset.tags || '').split(',').map(handleize).filter(Boolean),
        colors: (card.dataset.colors || '').split(',').map(normalizeColorKey).filter(Boolean),
        sizes: (card.dataset.sizes || '').split(',').map(handleize).filter(Boolean),
        availability: card.dataset.availability || 'in-stock',
        price,
        vendorLabel: card.dataset.vendor || '',
        typeLabel: card.dataset.productType || ''
      };
    }

    parseJsonProduct(raw) {
      const tags = (Array.isArray(raw.tags) ? raw.tags : String(raw.tags || '').split(','))
        .map((t) => handleize(String(t).trim()))
        .filter(Boolean);
      const colors = [];
      const sizes = [];
      const seenColors = new Set();
      const seenSizes = new Set();
      let minPrice = Infinity;

      (raw.variants || []).forEach((variant) => {
        const p = parseFloat(variant.price);
        if (!Number.isNaN(p) && p < minPrice) minPrice = p;
      });

      (raw.options || []).forEach((option) => {
        const name = String(option.name || '').toLowerCase();
        (option.values || []).forEach((value) => {
          const key = handleize(value);
          if (name === 'color' || name === 'colour') {
            const n = normalizeColorKey(key);
            if (n && !seenColors.has(n)) {
              seenColors.add(n);
              colors.push(n);
            }
          } else if (name === 'size') {
            if (key && key !== 'default-title' && !seenSizes.has(key)) {
              seenSizes.add(key);
              sizes.push(key);
            }
          }
        });
      });

      const available =
        raw.available === true || (raw.variants || []).some((v) => v.available);

      return {
        handle: raw.handle,
        vendor: handleize(raw.vendor || ''),
        vendorLabel: raw.vendor || '',
        type: handleize(raw.product_type || ''),
        typeLabel: raw.product_type || '',
        tags,
        colors,
        sizes,
        availability: available ? 'in-stock' : 'out-of-stock',
        price: minPrice === Infinity ? 0 : minPrice / 100
      };
    }

    indexFromDom() {
      this.products = [];
      this.byHandle.clear();
      this.initialGridHandles = new Set();
      this.gridItems.clear();
      this.refreshProductIndexFromDom();
      this.log('dom-index', { count: this.products.length });
    }

    refreshProductIndexFromDom() {
      if (!this.grid) return;
      this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const handle = li.getAttribute('data-product-handle');
        const card = li.querySelector('.collection-product-card');
        if (!handle || !card) return;

        const data = this.parseCardElement(card);
        data.handle = handle;
        if (!this.isSellableProduct(data)) {
          this.setItemVisible(li, false);
          return;
        }

        this.initialGridHandles.add(handle);
        this.gridItems.set(handle, li);
        if (!this.cardHtmlCache.has(handle)) {
          this.cardHtmlCache.set(handle, minifyCachedCardHtml(li.outerHTML));
        }
        this.addProduct(data);
      });
    }

    buildGridIndex() {
      this.gridItems.clear();
      if (!this.grid) return;
      this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const handle = li.getAttribute('data-product-handle');
        if (handle) this.gridItems.set(handle, li);
      });
    }

    parseCatalogEntry(raw) {
      const tags = (Array.isArray(raw.g) ? raw.g : Array.isArray(raw.tags) ? raw.tags : [])
        .map((t) => handleize(String(t).trim()))
        .filter(Boolean);
      const colors = (Array.isArray(raw.c) ? raw.c : Array.isArray(raw.colors) ? raw.colors : [])
        .map((c) => normalizeColorKey(handleize(String(c).trim())))
        .filter(Boolean);
      const sizes = (Array.isArray(raw.s) ? raw.s : Array.isArray(raw.sizes) ? raw.sizes : [])
        .map((s) => handleize(String(s).trim()))
        .filter((s) => s && s !== 'default-title');
      const vendorRaw = raw.v ?? raw.vendor ?? '';
      const typeRaw = raw.t ?? raw.type ?? '';
      const availability =
        raw.a === 0 || raw.a === '0' || raw.availability === 'out-of-stock'
          ? 'out-of-stock'
          : 'in-stock';

      return {
        handle: raw.h || raw.handle,
        vendor: handleize(vendorRaw),
        vendorLabel: raw.vendorLabel || vendorRaw || '',
        type: handleize(typeRaw),
        typeLabel: raw.typeLabel || typeRaw || '',
        tags,
        colors,
        sizes,
        availability,
        price: parseFloat(raw.p ?? raw.price) || 0
      };
    }

    async fetchCatalogPage(page) {
      const url = new URL(this.config.collectionUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('sections', this.config.sectionApiId);
      url.searchParams.set('nav_col_catalog', '1');
      url.searchParams.set('page', String(page));
      mergeSearchContextParams(url.searchParams, this.config);
      mergeEditorSearchParams(url.searchParams);

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error('catalog section ' + res.status);
      const data = await res.json();
      const raw = data[this.config.sectionApiId] || Object.values(data)[0] || '{}';
      return normalizeCatalogPayload(JSON.parse(raw));
    }

    async fetchProductsJsonPage(base, page, sort) {
      const url = new URL(base + '/products.json', window.location.origin);
      url.searchParams.set('limit', String(CATALOG_BATCH));
      url.searchParams.set('page', String(page));
      if (sort) url.searchParams.set('sort_by', sort);
      mergeEditorSearchParams(url.searchParams);

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error('products.json ' + res.status);
      const data = await res.json();
      return (data.products || []).map((raw) => this.parseJsonProduct(raw));
    }

    ingestCatalogProducts(products) {
      if (!products || !products.length) return;
      products.forEach((raw) => {
        const product = raw.handle ? this.parseCatalogEntry(raw) : raw;
        this.addProduct(product);
      });
    }

    async loadFullCatalog() {
      if (this.catalogPromise) return this.catalogPromise;
      if (!this.config || !this.config.collectionUrl) return this.products;
      if (!this.needsFullCatalog()) {
        this.fullCatalogLoaded = true;
        this.log('catalog-skip', { reason: 'all products already on page' });
        return this.products;
      }

      this.catalogPromise = (async () => {
        const expected = this.getExpectedCount();
        const catalogPages = expected
          ? Math.min(40, Math.ceil(expected / CATALOG_BATCH))
          : 1;
        let loaded = false;

        try {
          const pageResults = [];
          const first = await this.fetchCatalogPage(1);
          const totalPages = Math.min(40, Math.max(1, first.pages || 1));
          pageResults.push(first);

          if (totalPages > 1) {
            const remainingPages = [];
            for (let page = 2; page <= totalPages; page += 1) {
              remainingPages.push(page);
            }
            const rest = await runPool(remainingPages, FETCH_CONCURRENCY, (page) =>
              this.fetchCatalogPage(page)
            );
            pageResults.push(...rest);
          }

          const products = pageResults.flatMap((result) => result.products || []);
          if (products.length) {
            this.products = [];
            this.byHandle.clear();
            this.ingestCatalogProducts(products);
            loaded = true;
          }
        } catch (error) {
          this.log('catalog-section-fallback', { message: error.message });
        }

        if (!loaded && !(this.config && this.config.searchMode)) {
          const base = this.config.collectionUrl.replace(/\/$/, '');
          const sort = new URLSearchParams(window.location.search).get('sort_by');
          const jsonRequests = [];
          for (let page = 1; page <= catalogPages; page += 1) {
            jsonRequests.push(this.fetchProductsJsonPage(base, page, sort));
          }
          const jsonBatches = await Promise.all(jsonRequests);
          const fetched = jsonBatches.flat();
          if (fetched.length) {
            this.products = [];
            this.byHandle.clear();
            fetched.forEach((p) => this.addProduct(p));
          }
        }

        this.fullCatalogLoaded = true;
        this.log('catalog-ready', { count: this.products.length });

        if (this.active.size > 0) {
          void this.apply({ resetPage: false });
        }

        return this.products;
      })();

      try {
        return await this.catalogPromise;
      } catch (error) {
        console.warn('[NavColFilters] catalog load failed, using DOM index', error);
        this.fullCatalogLoaded = true;
        return this.products;
      }
    }

    /* ——— Filter matching ——— */

    getTagHandlesForFilter(filterKey, selectedValues) {
      const group = (this.config.tagFilterGroups || []).find(
        (g) => 'tag_' + g.id === filterKey
      );
      const selected = Array.from(selectedValues || []).map(handleize).filter(Boolean);

      if (!selected.length) return [];

      // Backward compatibility: old URLs/value used group id.
      if (group && group.tagHandles && group.tagHandles.length) {
        const groupIdHandle = handleize(group.id);
        if (selected.includes(groupIdHandle)) {
          return resolveTagFilterHandles(group.tagHandles, group);
        }
      }

      return resolveTagFilterHandles(selectedValues, group);
    }

    matches(product, active) {
      const activeMap = active || this.active;
      for (const [key, val] of activeMap) {
        if (key === 'price') {
          const { min, max } = val;
          if (product.price < min || product.price > max) return false;
          continue;
        }
        if (key === 'vendor') {
          const wanted = Array.from(val).map(handleize);
          if (!wanted.includes(product.vendor)) return false;
          continue;
        }
        if (key === 'product_type') {
          const wanted = Array.from(val).map(handleize);
          if (!wanted.includes(product.type)) return false;
          continue;
        }
        if (key === 'availability') {
          if (!Array.from(val).includes(product.availability)) return false;
          continue;
        }
        if (key === 'size') {
          const wanted = Array.from(val)
            .flatMap((v) => String(v).split('|'))
            .map(handleize)
            .filter(Boolean);
          const productHandles = this.getProductSizeHandles(product);
          if (!wanted.some((s) => productHandles.includes(s))) return false;
          continue;
        }
        if (key === 'color') {
          const wanted = Array.from(val)
            .flatMap((v) => String(v).split('|'))
            .map(normalizeColorKey)
            .filter(Boolean);
          if (!wanted.some((c) => product.colors.includes(c))) return false;
          continue;
        }
        if (key.startsWith('tag_')) {
          const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === key);
          if (group && (group.display === 'color' || group.layout === 'swatches')) {
            const wanted = Array.from(val)
              .flatMap((v) => String(v).split('|'))
              .map(normalizeColorKey)
              .filter(Boolean);
            if (!wanted.some((c) => product.colors.includes(c))) return false;
          } else {
            const matchTags = this.getTagHandlesForFilter(key, val);
            if (!matchTags.length) return false;
            const tagPrefix = group && group.tagPrefix ? group.tagPrefix : '';
            if (!productMatchesTagHandles(product, matchTags, tagPrefix)) return false;
          }
          continue;
        }
      }
      return true;
    }

    getMatches(active) {
      const matcher = this.compileMatcher(active);
      const results = [];
      const products = this.products;
      for (let i = 0; i < products.length; i += 1) {
        if (matcher(products[i])) results.push(products[i]);
      }
      return results;
    }

    compileMatcher(active) {
      const activeMap = active || this.active;
      const checks = [];

      activeMap.forEach((val, key) => {
        if (key === 'price') {
          const { min, max } = val;
          checks.push((product) => product.price >= min && product.price <= max);
          return;
        }
        if (key === 'vendor') {
          const wanted = new Set(Array.from(val).map(handleize));
          checks.push((product) => wanted.has(product.vendor));
          return;
        }
        if (key === 'product_type') {
          const wanted = new Set(Array.from(val).map(handleize));
          checks.push((product) => wanted.has(product.type));
          return;
        }
        if (key === 'availability') {
          const wanted = new Set(Array.from(val));
          checks.push((product) => wanted.has(product.availability));
          return;
        }
        if (key === 'size') {
          const wanted = Array.from(val)
            .flatMap((v) => String(v).split('|'))
            .map(handleize)
            .filter(Boolean);
          checks.push((product) => {
            const productHandles = this.getProductSizeHandles(product);
            return wanted.some((s) => productHandles.includes(s));
          });
          return;
        }
        if (key === 'color') {
          const wanted = Array.from(val)
            .flatMap((v) => String(v).split('|'))
            .map(normalizeColorKey)
            .filter(Boolean);
          checks.push((product) => wanted.some((c) => product.colors.includes(c)));
          return;
        }
        if (key.startsWith('tag_')) {
          const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === key);
          if (group && (group.display === 'color' || group.layout === 'swatches')) {
            const wanted = Array.from(val)
              .flatMap((v) => String(v).split('|'))
              .map(normalizeColorKey)
              .filter(Boolean);
            checks.push((product) => wanted.some((c) => product.colors.includes(c)));
          } else {
            const matchTags = this.getTagHandlesForFilter(key, val);
            if (!matchTags.length) {
              checks.push(() => false);
            } else {
              const tagPrefix = group && group.tagPrefix ? group.tagPrefix : '';
              checks.push((product) => productMatchesTagHandles(product, matchTags, tagPrefix));
            }
          }
        }
      });

      if (!checks.length) {
        if (this.shouldHideSoldOut()) {
          return (product) => this.isSellableProduct(product);
        }
        return () => true;
      }
      if (this.shouldHideSoldOut()) {
        checks.push((product) => this.isSellableProduct(product));
      }
      return (product) => {
        for (let i = 0; i < checks.length; i += 1) {
          if (!checks[i](product)) return false;
        }
        return true;
      };
    }

    buildFacetActive(filterKey, optionValue) {
      const trial = new Map();
      this.active.forEach((val, key) => {
        if (key === filterKey) return;
        if (val instanceof Set) trial.set(key, new Set(val));
        else trial.set(key, val);
      });
      trial.set(filterKey, new Set([optionValue]));
      return trial;
    }

    countFacetMatches(active) {
      return this.getMatches(active).length;
    }

    clearFacetPoolCache() {
      this._facetPoolCache = new Map();
    }

    getActiveExcluding(excludeKey) {
      const trial = new Map();
      this.active.forEach((val, key) => {
        if (key === excludeKey) return;
        if (val instanceof Set) trial.set(key, new Set(val));
        else trial.set(key, val);
      });
      return trial;
    }

    getPoolExcluding(excludeKey) {
      if (!this._facetPoolCache) this._facetPoolCache = new Map();
      if (this._facetPoolCache.has(excludeKey)) return this._facetPoolCache.get(excludeKey);

      const trial = this.getActiveExcluding(excludeKey);
      const pool =
        trial.size === 0 ? this.products : this.products.filter(this.compileMatcher(trial));
      this._facetPoolCache.set(excludeKey, pool);
      return pool;
    }

    countFacetForTagOption(filterKey, optionValue) {
      const pool = this.getPoolExcluding(filterKey);
      const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === filterKey);
      const tagPrefix = group && group.tagPrefix ? group.tagPrefix : '';
      const matchTags = this.getTagHandlesForFilter(filterKey, new Set([optionValue]));
      if (!matchTags.length) return 0;
      let count = 0;
      for (let i = 0; i < pool.length; i += 1) {
        if (productMatchesTagHandles(pool[i], matchTags, tagPrefix)) count += 1;
      }
      return count;
    }

    countFacetForProductType(typeHandle) {
      const wanted = handleize(typeHandle);
      const pool = this.getPoolExcluding('product_type');
      let count = 0;
      for (let i = 0; i < pool.length; i += 1) {
        if (pool[i].type === wanted) count += 1;
      }
      return count;
    }

    countFacetForSize(sizeHandle) {
      const wanted = String(sizeHandle)
        .split('|')
        .map(handleize)
        .filter(Boolean);
      if (!wanted.length) return 0;
      const pool = this.getPoolExcluding('size');
      let count = 0;
      for (let i = 0; i < pool.length; i += 1) {
        const productHandles = this.getProductSizeHandles(pool[i]);
        if (wanted.some((s) => productHandles.includes(s))) count += 1;
      }
      return count;
    }

    countFacetForColor(colorValue) {
      const wanted = String(colorValue)
        .split('|')
        .map(normalizeColorKey)
        .filter(Boolean);
      if (!wanted.length) return 0;
      const pool = this.getPoolExcluding('color');
      let count = 0;
      for (let i = 0; i < pool.length; i += 1) {
        if (wanted.some((c) => pool[i].colors.includes(c))) count += 1;
      }
      return count;
    }

    countFacetForVendor(vendorHandle) {
      const wanted = handleize(vendorHandle);
      const pool = this.getPoolExcluding('vendor');
      let count = 0;
      for (let i = 0; i < pool.length; i += 1) {
        if (pool[i].vendor === wanted) count += 1;
      }
      return count;
    }

    countFacetForAvailability(value) {
      const pool = this.getPoolExcluding('availability');
      let count = 0;
      for (let i = 0; i < pool.length; i += 1) {
        if (pool[i].availability === value) count += 1;
      }
      return count;
    }

    getMatchingHandlesFromDom() {
      const handles = [];
      if (!this.grid) return handles;
      this.grid.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const card = li.querySelector('.collection-product-card');
        if (!card) return;
        const data = this.parseCardElement(card);
        if (this.matches(data)) {
          handles.push(li.getAttribute('data-product-handle'));
        }
      });
      return handles;
    }

    /* ——— Active filter state ——— */

    readActiveFromDom() {
      const next = new Map();
      this.filtersEl.querySelectorAll('.filter-checkbox:checked').forEach((input) => {
        if (!next.has(input.name)) next.set(input.name, new Set());
        next.get(input.name).add(input.value);
      });

      const minEl = this.filtersEl.querySelector('input[name="price_min"]');
      const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
      const min = minEl ? parseFloat(minEl.value) || 0 : 0;
      const max = maxEl ? parseFloat(maxEl.value) || Infinity : Infinity;
      if (min > 0 || max < Infinity) {
        next.set('price', { min, max });
      }

      this.active = next;
      this.clearFacetPoolCache();
      this.syncTagBoxState();
    }

    collectActiveFromDom() {
      const next = new Map();
      if (!this.filtersEl) return next;

      this.filtersEl.querySelectorAll('.filter-checkbox:checked').forEach((input) => {
        if (!next.has(input.name)) next.set(input.name, new Set());
        next.get(input.name).add(input.value);
      });

      const minEl = this.filtersEl.querySelector('input[name="price_min"]');
      const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
      const min = minEl ? parseFloat(minEl.value) || 0 : 0;
      const max = maxEl ? parseFloat(maxEl.value) || Infinity : Infinity;
      if (min > 0 || max < Infinity) {
        next.set('price', { min, max });
      }

      return next;
    }

    syncActiveFiltersFromDom() {
      this.readActiveFromDom();
    }

    syncTagBoxState() {
      this.filtersEl.querySelectorAll('.filter-option--tag-box, .filter-option--size-box').forEach((option) => {
        const input = option.querySelector('.filter-checkbox');
        option.classList.toggle('is-selected', !!(input && input.checked));
      });
    }

    isFilterOptionVisible(option) {
      return !!(option && !option.hidden && !option.classList.contains('is-zero-count'));
    }

    setFilterOptionVisible(option, visible) {
      if (!option) return;
      option.hidden = !visible;
      option.classList.toggle('is-zero-count', !visible);
      option.style.removeProperty('display');
      option.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    shouldShowFilterCounts(container) {
      if (!container) return false;
      return container.dataset.showFilterCounts === 'true';
    }

    ensureFilterCountElement(option) {
      if (!option || option.querySelector('.filter-count')) return;
      if (!option.classList.contains('filter-option--split')) return;
      const countEl = document.createElement('span');
      countEl.className = 'filter-count';
      option.appendChild(countEl);
    }

    updateFilterOptionCount(option, checkbox, count, labelBase) {
      const container = option?.closest('[data-nav-col-filter]');
      const showCounts = this.shouldShowFilterCounts(container);
      const visible = count > 0;

      if (checkbox) {
        if (!visible) checkbox.checked = false;
        checkbox.disabled = !visible;
      }
      this.setFilterOptionVisible(option, visible);

      const label = option ? option.querySelector('.filter-label') : null;
      const base =
        labelBase ||
        label?.dataset.filterLabelBase ||
        (option?.classList.contains('filter-option--size-box')
          ? formatSizeLabel(checkbox?.value || '')
          : formatLabel(checkbox?.value || ''));

      if (label) {
        label.dataset.filterLabelBase = base;
        label.textContent = base;
      }

      if (!showCounts) return;

      if (option?.classList.contains('filter-option--tag-box')) {
        if (label) label.textContent = `${base} (${count})`;
        return;
      }

      this.ensureFilterCountElement(option);
      const countEl = option ? option.querySelector('.filter-count') : null;
      if (countEl) {
        countEl.textContent = `(${count})`;
        countEl.style.removeProperty('display');
      } else if (label) {
        label.textContent = `${base} (${count})`;
      }
    }

    buildSplitFilterOptionHtml(name, value, labelText, count, checked, options = {}) {
      const swatchHtml = options.swatchHtml || '';
      const checkboxClass = options.colorCheckbox ? 'filter-checkbox color-checkbox' : 'filter-checkbox';
      const showCounts = options.showCounts === true;
      const safeLabel = String(labelText || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
      return (
        `<span class="filter-option__leading">` +
        `<input type="checkbox" name="${name}" value="${value}" class="${checkboxClass}"` +
        (checked ? ' checked' : '') +
        `>${swatchHtml}` +
        `<span class="filter-label" data-filter-label-base="${safeLabel}">${labelText}</span>` +
        `</span>` +
        (showCounts ? `<span class="filter-count">(${count})</span>` : '')
      );
    }

    buildSizeBoxFilterOptionHtml(name, value, labelText, checked) {
      const safeLabel = String(labelText || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
      return (
        `<input type="checkbox" name="${name}" value="${value}" class="filter-checkbox"` +
        (checked ? ' checked' : '') +
        `><span class="filter-label" data-filter-label-base="${safeLabel}">${labelText}</span>`
      );
    }

    appendSizeFilterOption(container, value, labelText, count, checked) {
      if (!count) return;
      const label = document.createElement('label');
      label.className = 'filter-option filter-option--size-box';
      const showCounts = this.shouldShowFilterCounts(container);
      label.innerHTML = showCounts
        ? this.buildTagBoxFilterOptionHtml('size', value, labelText, count, checked, true)
        : this.buildSizeBoxFilterOptionHtml('size', value, labelText, checked);
      container.appendChild(label);
    }

    applySizeFilterLayout() {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="size"]');
      if (!container) return;
      container.classList.remove('filter-options--list');
      container.dataset.filterStyle = 'box';
      this.syncTagBoxState();
    }

    buildTagBoxFilterOptionHtml(name, value, labelText, count, checked, showCounts = false) {
      const safeLabel = String(labelText || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
      const countText = showCounts ? ` (${count})` : '';
      return (
        `<input type="checkbox" name="${name}" value="${value}" class="filter-checkbox"` +
        (checked ? ' checked' : '') +
        `><span class="filter-label" data-filter-label-base="${safeLabel}">${labelText}${countText}</span>`
      );
    }

    clearAll() {
      this._suppress = true;
      this.filtersEl.querySelectorAll('.filter-checkbox').forEach((c) => {
        c.checked = false;
      });
      this.filtersEl.querySelectorAll('.price-input').forEach((i) => {
        i.value = '';
      });
      this._suppress = false;
      this.active.clear();
      void this.restore();
    }

    /* ——— Apply / restore ——— */

    scheduleApply(options) {
      window.clearTimeout(this.applyTimer);
      this.applyTimer = window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          this.readActiveFromDom();
          if (this.active.size === 0) void this.restore();
          else this.applyFilters(options || { resetPage: true });
        });
      }, APPLY_DEBOUNCE_MS);
    }

    applyFilters(options) {
      this.readActiveFromDom();
      if (this.active.size === 0) {
        void this.restore();
        return;
      }
      void this.apply(options || { resetPage: true });
    }

    getBatchSize() {
      const n = parseInt(this.config && this.config.productsPerPage, 10);
      return Number.isFinite(n) && n > 0 ? n : 36;
    }

    getOrderedHandles() {
      if (this.filtered && this.orderedHandles.length) {
        return this.orderedHandles;
      }
      if (this.fullCatalogLoaded && this.products.length) {
        return this.products.filter((p) => this.isSellableProduct(p)).map((p) => p.handle);
      }
      return [...this.initialGridHandles];
    }

    initInfiniteScrollState() {
      this.orderedHandles = this.getOrderedHandles();
      this.visibleCount = Math.min(this.getBatchSize(), this.orderedHandles.length);
      this.removePagination();
      this.updateGridVisibility();
      void this.fetchVisibleBatchCards({ reorderAll: true });
      this.setupInfiniteScroll();
    }

    setupInfiniteScroll() {
      if (!this.grid || !this.section) return;

      let sentinel = this.ensureScrollSentinel();
      this.destroyScrollObserver();
      this.unbindScrollLoadMore();

      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          if (this.scrollObserver && sentinel) {
            this.scrollObserver.unobserve(sentinel);
          }
          void this.loadMore().finally(() => {
            if (
              this.scrollObserver &&
              sentinel &&
              this.visibleCount < this.orderedHandles.length
            ) {
              requestAnimationFrame(() => {
                if (this.scrollObserver && sentinel) {
                  this.scrollObserver.observe(sentinel);
                }
              });
            }
          });
        },
        { root: null, rootMargin: '800px 0px', threshold: 0 }
      );
      this.scrollObserver.observe(sentinel);
      this.bindScrollLoadMore();
      this.updateScrollSentinel();
    }

    ensureScrollSentinel() {
      let sentinel = this.section.querySelector('.nav-col-scroll-sentinel');
      if (!sentinel) {
        sentinel = document.createElement('div');
        sentinel.className = 'nav-col-scroll-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
      }
      if (this.grid && sentinel.parentNode && sentinel.previousElementSibling !== this.grid) {
        this.grid.insertAdjacentElement('afterend', sentinel);
      } else if (this.grid && !sentinel.parentNode) {
        this.grid.insertAdjacentElement('afterend', sentinel);
      }
      return sentinel;
    }

    bindScrollLoadMore() {
      this.unbindScrollLoadMore();
      this._scrollLoadHandler = () => {
        if (this.scrollLoading) return;
        if (this.visibleCount >= this.orderedHandles.length) return;
        const sentinel = this.section && this.section.querySelector('.nav-col-scroll-sentinel');
        if (!sentinel || sentinel.classList.contains('is-exhausted')) return;
        const rect = sentinel.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 800) {
          void this.loadMore();
        }
      };
      window.addEventListener('scroll', this._scrollLoadHandler, { passive: true });
      this.track(() => this.unbindScrollLoadMore());
    }

    unbindScrollLoadMore() {
      if (this._scrollLoadHandler) {
        window.removeEventListener('scroll', this._scrollLoadHandler);
        this._scrollLoadHandler = null;
      }
    }

    destroyScrollObserver() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect();
        this.scrollObserver = null;
      }
      this.unbindScrollLoadMore();
    }

    hasHandleInGrid(handle) {
      if (!handle) return false;
      if (this.gridItems.has(handle)) return true;
      if (!this.grid) return false;
      return !!this.grid.querySelector(`li[data-product-handle="${escapeSelector(handle)}"]`);
    }

    async appendHandlesToGrid(handles) {
      if (!handles || !handles.length) return 0;

      await this.ensureCardsCached(handles);
      this.appendCachedCards(handles);

      const stillMissing = this.getHandlesMissingFromGrid(handles);
      return handles.length - stillMissing.length;
    }

    updateScrollSentinel() {
      const sentinel = this.section && this.section.querySelector('.nav-col-scroll-sentinel');
      if (!sentinel) return;
      const total = this.orderedHandles.length;
      const hasMore = this.visibleCount < total;
      sentinel.hidden = false;
      sentinel.classList.toggle('is-exhausted', !hasMore);
      sentinel.style.display = hasMore ? '' : 'none';
      sentinel.classList.toggle('is-loading', this.scrollLoading);
      sentinel.textContent = this.scrollLoading ? 'Loading more products…' : '';
    }

    async warmEntireCollectionCardCache() {
      if (this.fullCardCacheWarmed) return;
      if (this.fullCardCachePromise) {
        await this.fullCardCachePromise;
        return;
      }

      const maxPages = this.getCollectionPageCount();
      const pagesToFetch = [];
      for (let page = 1; page <= maxPages; page += 1) {
        if (!this.cachedCardPages.has(page)) pagesToFetch.push(page);
      }

      if (!pagesToFetch.length) {
        this.fullCardCacheWarmed = true;
        return;
      }

      this.fullCardCachePromise = runPool(pagesToFetch, FETCH_CONCURRENCY, async (page) => {
        const cardsHtml = await this.fetchCollectionCardsPage(page);
        if (cardsHtml) this.cacheCardHtmlFromString(cardsHtml, null);
        this.cachedCardPages.add(page);
      });

      try {
        await this.fullCardCachePromise;
        if (this.cachedCardPages.size >= maxPages) {
          this.fullCardCacheWarmed = true;
        }
      } finally {
        this.fullCardCachePromise = null;
      }
    }

    getRenderableHandles(handles) {
      return (handles || []).filter((handle) => this.cardHtmlCache.has(handle));
    }

    async loadMore() {
      if (this.scrollLoading) return;
      if (this.visibleCount >= this.orderedHandles.length) {
        this.updateScrollSentinel();
        return;
      }

      this.scrollLoading = true;
      this.updateScrollSentinel();

      const previousVisible = this.visibleCount;
      const targetVisible = Math.min(
        previousVisible + this.getBatchSize(),
        this.orderedHandles.length
      );
      const batchHandles = this.orderedHandles.slice(previousVisible, targetVisible);

      try {
        const added = await this.appendHandlesToGrid(batchHandles);
        const allPresent = batchHandles.every((handle) => this.hasHandleInGrid(handle));

        if (!allPresent && added === 0) {
          await this.ensureCardsCached(batchHandles);
          const retryAdded = await this.appendHandlesToGrid(batchHandles);
          if (retryAdded > 0 || batchHandles.every((handle) => this.hasHandleInGrid(handle))) {
            this.visibleCount = targetVisible;
            reorderGridToHandles(
              this.grid,
              this.orderedHandles.slice(0, this.visibleCount),
              this.gridItems
            );
            this.updateGridVisibility();
            this.syncGridUi();
          } else {
            this.debug.warn('load-more-empty', {
              batch: batchHandles.length,
              from: previousVisible,
              to: targetVisible
            });
          }
        } else if (added > 0 || allPresent) {
          this.visibleCount = targetVisible;
          reorderGridToHandles(
            this.grid,
            this.orderedHandles.slice(0, this.visibleCount),
            this.gridItems
          );
          this.updateGridVisibility();
          this.syncGridUi();
        } else {
          this.debug.warn('load-more-empty', {
            batch: batchHandles.length,
            from: previousVisible,
            to: targetVisible
          });
        }
      } catch (error) {
        console.error('[NavColFilters] load-more failed', error);
      } finally {
        this.scrollLoading = false;
        this.updateScrollSentinel();
      }
    }

    updateGridVisibility() {
      const matchSet = new Set(this.orderedHandles);
      const showHandles = this.orderedHandles.slice(0, this.visibleCount);
      const showSet = new Set(showHandles);

      if (!this.gridItems.size) this.buildGridIndex();
      this.gridItems.forEach((li, handle) => {
        this.setItemVisible(li, matchSet.has(handle) && showSet.has(handle));
      });

      return showHandles;
    }

    getHandlesMissingFromGrid(handles) {
      return (handles || []).filter((h) => !this.hasHandleInGrid(h));
    }

    appendCachedCards(handles) {
      const parts = [];
      (handles || []).forEach((handle) => {
        const html = this.cardHtmlCache.get(handle);
        if (html) parts.push(html);
      });
      if (parts.length) appendProductCards(this.grid, parts.join(''), this.gridItems);
    }

    needsNetworkForHandles(handles) {
      return (handles || []).some((handle) => handle && !this.cardHtmlCache.has(handle));
    }

    async fetchVisibleBatchCards({ reorderAll = false } = {}) {
      const showHandles = this.orderedHandles.slice(0, this.visibleCount);
      const needsFetch = this.needsNetworkForHandles(showHandles);

      if (needsFetch) this.grid.classList.add('nav-col-filter-loading');
      try {
        await this.appendHandlesToGrid(showHandles);
        if (reorderAll) reorderGridToHandles(this.grid, showHandles, this.gridItems);
        this.updateGridVisibility();
        this.syncGridUi();
        this.updateScrollSentinel();
      } catch (error) {
        console.error('[NavColFilters] visible batch fetch failed', error);
      } finally {
        if (needsFetch) this.grid.classList.remove('nav-col-filter-loading');
      }
    }

    async renderVisibleBatch({ reorderAll = false } = {}) {
      this.updateGridVisibility();
      await this.fetchVisibleBatchCards({ reorderAll });
      this.updateScrollSentinel();
    }

    removePagination() {
      if (!this.section) return;
      this.section.querySelectorAll('.pagination, .nav-col-client-pagination').forEach((el) => {
        el.remove();
      });
    }

    setItemVisible(li, visible) {
      li.classList.toggle('nav-col-filter-hidden', !visible);
      // Also enforce inline visibility to recover from older scripts that used style.display.
      li.style.display = visible ? '' : 'none';
    }

    async apply({ resetPage = true } = {}) {
      if (this._applying) {
        this.debug.warn('apply-skipped', { reason: 'already-applying' });
        return;
      }
      this._applying = true;

      try {
        if (!this.fullCatalogLoaded && this.needsFullCatalog()) {
          await this.ensureFullCatalogLoad();
        }

        const allMatches = this.getMatches().map((p) => p.handle);

        this.log('apply', {
          active: Object.fromEntries(
            [...this.active.entries()].map(([k, v]) => [k, v instanceof Set ? [...v] : v])
          ),
          matches: allMatches.length
        });

        this.filtered = true;
        this.orderedHandles = allMatches;
        this.clearFacetPoolCache();
        this.destroyScrollObserver();

        const matchSet = new Set(allMatches);
        this.visibleCount = Math.min(this.getBatchSize(), allMatches.length);
        const showSet = new Set(allMatches.slice(0, this.visibleCount));
        if (!this.gridItems.size) this.buildGridIndex();
        this.gridItems.forEach((li, handle) => {
          this.setItemVisible(li, matchSet.has(handle) && showSet.has(handle));
        });

        this.finishApply(allMatches.length);
        this.scheduleFacetSync();
        this.setupInfiniteScroll();

        void this.fetchVisibleBatchCards({ reorderAll: true }).then(() => {
          this.pushDebugSnapshot({
            lastMatchCount: allMatches.length,
            lastVisible: this.grid.querySelectorAll(
              'li[data-product-handle]:not(.nav-col-filter-hidden)'
            ).length,
            cardsCached: this.cardHtmlCache.size
          });
          this.updateScrollSentinel();
        });
      } catch (error) {
        this.debug.error('apply-failed', { message: error.message, stack: error.stack });
        throw error;
      } finally {
        this._applying = false;
      }
    }

    syncGridUi() {
      window.requestAnimationFrame(() => {
        if (window.bindCollectionProductCardInteractions) {
          window.bindCollectionProductCardInteractions(this.grid);
        } else if (window.initCollectionProductCardImages) {
          window.initCollectionProductCardImages(this.grid);
        }
        if (window.syncNavCollectionWishlistButtons) {
          window.syncNavCollectionWishlistButtons(this.grid);
        }
      });
    }

    finishApply(matchCount) {
      this.clearUrlFilterPendingState();
      this.updateNoResults(matchCount);
      this.removePagination();
      this.updateUrl(1);
      this.updateResultsCount(matchCount);
      this.updateFilterCount();
      this.updateChips();
      this.pushDebugSnapshot({ lastMatchCount: matchCount, lastVisible: this.visibleCount });
      this.syncGridUi();
    }

    async loadOffPageProducts(missingHandles, orderHandles) {
      if (!missingHandles.length) return;

      try {
        const html = await this.fetchCards(missingHandles);
        if (!html) return;

        appendProductCards(this.grid, html, this.gridItems);
        reorderGridToHandles(this.grid, orderHandles, this.gridItems);
        this.updateGridVisibility();
        this.syncGridUi();
      } catch (error) {
        console.error('[NavColFilters] off-page load failed', error);
      }
    }

    cacheCardHtmlFromString(html, wantedSet) {
      if (!html) return 0;
      let cached = 0;
      const temp = document.createElement('div');
      temp.innerHTML = html;
      temp.querySelectorAll('li[data-product-handle]').forEach((li) => {
        const handle = li.getAttribute('data-product-handle');
        if (!handle) return;
        if (wantedSet && !wantedSet.has(handle)) return;
        if (this.cardHtmlCache.has(handle)) return;
        const outer = minifyCachedCardHtml(li.outerHTML);
        this.cardHtmlCache.set(handle, outer);
        cached += 1;
      });
      return cached;
    }

    getCollectionPageCount() {
      const expected = this.getExpectedCount();
      const perPage = parseInt(this.config && this.config.productsPerPage, 10);
      const pageSize = Number.isFinite(perPage) && perPage > 0 ? perPage : 36;
      return expected ? Math.min(40, Math.ceil(expected / pageSize)) : 40;
    }

    async fetchCollectionCardsPage(page) {
      const url = new URL(this.config.collectionUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('sections', this.config.sectionApiId);
      url.searchParams.set('nav_col_render', '1');
      url.searchParams.set('nav_col_render_all', '1');
      url.searchParams.set('nav_col_lite', '1');
      url.searchParams.set('page', String(page));
      mergeSearchContextParams(url.searchParams, this.config);
      mergeEditorSearchParams(url.searchParams);

      const res = await fetch(url.toString(), { cache: 'no-store' });
      if (!res.ok) throw new Error('section render failed');
      const data = await res.json();
      const html = data[this.config.sectionApiId] || Object.values(data)[0] || '';
      return stripCardHtml(html);
    }

    async fetchCardsByHandles(handles) {
      const missing = [...new Set(handles.filter((h) => h && !this.cardHtmlCache.has(h)))];
      if (!missing.length || !this.config) return;

      const batches = [];
      for (let i = 0; i < missing.length; i += FETCH_HANDLE_BATCH) {
        batches.push(missing.slice(i, i + FETCH_HANDLE_BATCH));
      }

      await runPool(batches, FETCH_CONCURRENCY, async (batch) => {
        if (!batch.length) return;
        const url = new URL(this.config.collectionUrl || window.location.pathname, window.location.origin);
        url.searchParams.set('sections', this.config.sectionApiId);
        url.searchParams.set('nav_col_render', '1');
        url.searchParams.set('nav_col_lite', '1');
        url.searchParams.set('filtered_handles', batch.join(','));
        mergeSearchContextParams(url.searchParams, this.config);
        mergeEditorSearchParams(url.searchParams);

        const res = await fetch(url.toString(), { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        const html = stripCardHtml(data[this.config.sectionApiId] || Object.values(data)[0] || '');
        if (html) this.cacheCardHtmlFromString(html, new Set(batch));
      });
    }

    async ensureCardsCached(handles) {
      const wanted = [...new Set((handles || []).filter(Boolean))];
      if (!wanted.length || !this.config) return;

      const missing = wanted.filter((handle) => !this.cardHtmlCache.has(handle));
      if (!missing.length) return;

      if (missing.length <= TARGETED_HANDLE_FETCH_MAX) {
        await this.fetchCardsByHandles(missing);
        if (missing.every((handle) => this.cardHtmlCache.has(handle))) return;
      }

      const missingSet = new Set(missing.filter((handle) => !this.cardHtmlCache.has(handle)));
      if (!missingSet.size) return;

      const maxPages = this.getCollectionPageCount();
      const pagesToFetch = [];
      for (let page = 1; page <= maxPages; page += 1) {
        if (this.cachedCardPages.has(page)) {
          [...missingSet].forEach((handle) => {
            if (this.cardHtmlCache.has(handle)) missingSet.delete(handle);
          });
          if (!missingSet.size) break;
          continue;
        }
        pagesToFetch.push(page);
      }

      if (!missingSet.size || !pagesToFetch.length) return;

      await runPool(pagesToFetch, FETCH_CONCURRENCY, async (page) => {
        const cardsHtml = await this.fetchCollectionCardsPage(page);
        this.cachedCardPages.add(page);
        if (cardsHtml) this.cacheCardHtmlFromString(cardsHtml, null);
      });
    }

    async fetchCards(handles) {
      if (!handles.length || !this.config) return '';

      const wanted = [...new Set(handles.filter(Boolean))];
      if (!wanted.length) return '';

      await this.ensureCardsCached(wanted);

      return wanted
        .map((handle) => this.cardHtmlCache.get(handle))
        .filter(Boolean)
        .join('');
    }

    async restore() {
      this.filtered = false;
      this.clearFacetPoolCache();
      this.removePagination();
      this.orderedHandles = this.getOrderedHandles();
      this.visibleCount = Math.min(this.getBatchSize(), this.orderedHandles.length);
      this.destroyScrollObserver();
      this.updateGridVisibility();

      this.updateNoResults(this.orderedHandles.length);
      this.updateUrl(1, true);
      this.updateResultsCount(this.orderedHandles.length);
      this.updateFilterCount();
      this.updateChips();
      this.scheduleFacetSync();

      await this.fetchVisibleBatchCards({ reorderAll: true });
      this.setupInfiniteScroll();
      this.syncGridUi();
      this.updateScrollSentinel();
    }

    /* ——— Filter UI build ——— */

    facetCounts() {
      const counts = {
        availability: { 'in-stock': 0, 'out-of-stock': 0 },
        vendor: new Map(),
        product_type: new Map(),
        size: new Map(),
        tag: new Map()
      };
      this.products.forEach((p) => {
        if (!this.isSellableProduct(p)) return;
        counts.availability[p.availability] += 1;
        if (p.vendor) counts.vendor.set(p.vendor, (counts.vendor.get(p.vendor) || 0) + 1);
        if (p.type) counts.product_type.set(p.type, (counts.product_type.get(p.type) || 0) + 1);
        p.sizes.forEach((s) => counts.size.set(s, (counts.size.get(s) || 0) + 1));
        p.tags.forEach((t) => counts.tag.set(t, (counts.tag.get(t) || 0) + 1));
      });
      return counts;
    }

    syncFilterOptions() {
      const counts = this.facetCounts();
      this.buildCheckboxGroup('vendor', counts.vendor, (h) => {
        const match = this.products.find((p) => p.vendor === h);
        const rawLabel = (match && match.vendorLabel) || h;
        return formatLabel(handleize(rawLabel));
      });
      this.buildCheckboxGroup('product_type', counts.product_type, (h) => {
        const match = this.products.find((p) => p.type === h);
        const rawLabel = (match && match.typeLabel) || h;
        return formatLabel(handleize(rawLabel));
      });
      this.buildSizeFilters();
      this.buildColorFilters();
      this.applyTagFilterLayouts();
      this.applyProductTypeFilterLayout();
      if (this.shouldHideSoldOut()) {
        this.filtersEl.querySelectorAll('[data-nav-col-filter="availability"]').forEach((el) => {
          const group = el.closest('.filter-group');
          if (group) group.hidden = true;
        });
      }
      this.syncAllFacetCounts();
      this._optionsBuilt = true;

      if (this._urlFiltersPending) {
        this.applyActiveFiltersToDom();
        void this.tryApplyUrlFilters();
      }
    }

    buildCheckboxGroup(name, countMap, labelFn, naturalSort) {
      const container = this.filtersEl.querySelector(`[data-nav-col-filter="${name}"]`);
      if (!container || container.dataset.serverRendered === 'true') return;

      const checked = new Set();
      this.filtersEl.querySelectorAll(`input[name="${name}"]:checked`).forEach((i) => checked.add(i.value));

      const entries = [...countMap.entries()].filter(([, c]) => c > 0);
      if (naturalSort) {
        entries.sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }));
      } else {
        entries.sort((a, b) => labelFn(a[0]).localeCompare(labelFn(b[0])));
      }

      container.innerHTML = '';
      const isProductType = name === 'product_type';
      const useBoxStyle = isProductType && this.isProductTypeBoxStyle();
      const showCounts = this.shouldShowFilterCounts(container);

      entries.forEach(([value, count]) => {
        const label = document.createElement('label');
        const labelText = labelFn(value);
        if (isProductType && useBoxStyle) {
          label.className = 'filter-option filter-option--tag-box';
          label.innerHTML = this.buildTagBoxFilterOptionHtml(
            name,
            value,
            labelText,
            count,
            checked.has(value),
            showCounts
          );
        } else {
          label.className = 'filter-option filter-option--split';
          label.innerHTML = this.buildSplitFilterOptionHtml(
            name,
            value,
            labelText,
            count,
            checked.has(value),
            { showCounts }
          );
        }
        container.appendChild(label);
      });

      if (isProductType) {
        this.applyProductTypeFilterLayout();
      }
    }

    getSizeGroupHandleIndex() {
      if (!this._sizeGroupHandleIndex) {
        const index = new Set();
        ((this.config && this.config.sizeGroups) || []).forEach((group) => {
          String(group.value || '')
            .split('|')
            .map(handleize)
            .filter(Boolean)
            .forEach((h) => index.add(h));
        });
        this._sizeGroupHandleIndex = index;
      }
      return this._sizeGroupHandleIndex;
    }

    getProductSizeHandles(product) {
      const handles = new Set(product.sizes || []);
      const groupHandles = this.getSizeGroupHandleIndex();
      if (groupHandles.size && product.tags && product.tags.length) {
        product.tags.forEach((tag) => {
          if (groupHandles.has(tag)) handles.add(tag);
        });
      }
      return [...handles];
    }

    buildSizeFilters() {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="size"]');
      if (!container || container.dataset.serverRendered === 'true') return;

      const checked = new Set();
      this.filtersEl.querySelectorAll('input[name="size"]:checked').forEach((i) => checked.add(i.value));
      const grouped = new Set();
      const groups = (this.config && this.config.sizeGroups) || [];
      const options = [];

      container.innerHTML = '';

      if (groups.length) {
        groups.forEach((group) => {
          const handles = String(group.value || '')
            .split('|')
            .map(handleize)
            .filter(Boolean);
          const count = this.products.filter((p) =>
            handles.some((h) => this.getProductSizeHandles(p).includes(h))
          ).length;
          if (!count) return;
          handles.forEach((h) => grouped.add(h));
          options.push({
            value: group.value,
            label: group.label,
            count,
            checked: checked.has(group.value),
            sortKey: group.label || group.labelHandle || handles[0] || group.value
          });
        });

        const solo = new Map();
        this.products.forEach((p) => {
          this.getProductSizeHandles(p).forEach((s) => {
            if (!grouped.has(s)) solo.set(s, (solo.get(s) || 0) + 1);
          });
        });

        solo.forEach((count, value) => {
          options.push({
            value,
            label: formatSizeLabel(value),
            count,
            checked: checked.has(value),
            sortKey: value
          });
        });
      } else {
        const counts = new Map();
        this.products.forEach((p) => {
          p.sizes.forEach((s) => counts.set(s, (counts.get(s) || 0) + 1));
        });

        counts.forEach((count, value) => {
          if (!count) return;
          options.push({
            value,
            label: formatSizeLabel(value),
            count,
            checked: checked.has(value),
            sortKey: value
          });
        });
      }

      options.sort((a, b) => compareSizeOrder(a.sortKey, b.sortKey));
      options.forEach((option) => {
        this.appendSizeFilterOption(
          container,
          option.value,
          option.label,
          option.count,
          option.checked
        );
      });

      this.applySizeFilterLayout();
    }

    buildColorFilters() {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="color"]');
      if (!container || container.dataset.serverRendered === 'true') return;

      const checked = new Set();
      this.filtersEl.querySelectorAll('input[name="color"]:checked').forEach((i) => checked.add(i.value));
      const grouped = new Set();
      const groups = (this.config && this.config.colorGroups) || [];

      container.innerHTML = '';
      const showCounts = this.shouldShowFilterCounts(container);

      groups.forEach((group) => {
        const handles = String(group.value || '')
          .split('|')
          .map(normalizeColorKey)
          .filter(Boolean);
        const count = this.products.filter((p) =>
          handles.some((h) => p.colors.includes(h))
        ).length;
        if (!count) return;
        handles.forEach((h) => grouped.add(h));
        const label = document.createElement('label');
        label.className = 'filter-option filter-option--color filter-option--split';
        label.innerHTML = this.buildSplitFilterOptionHtml('color', group.value, group.label, count, checked.has(group.value), {
          swatchHtml: `<span class="color-swatch" style="${group.swatchStyle || ''}"></span>`,
          colorCheckbox: true,
          showCounts
        });
        container.appendChild(label);
      });

      const solo = new Map();
      this.products.forEach((p) => {
        p.colors.forEach((c) => {
          if (!grouped.has(c)) solo.set(c, (solo.get(c) || 0) + 1);
        });
      });

      [...solo.entries()]
        .sort((a, b) => formatLabel(a[0]).localeCompare(formatLabel(b[0])))
        .forEach(([value, count]) => {
          const label = document.createElement('label');
          label.className = 'filter-option filter-option--color filter-option--split';
          const labelText = formatLabel(value);
          label.innerHTML = this.buildSplitFilterOptionHtml('color', value, labelText, count, checked.has(value), {
            swatchHtml: `<span class="color-swatch" style="background-color:${formatLabel(value)};"></span>`,
            colorCheckbox: true,
            showCounts
          });
          container.appendChild(label);
        });
    }

    isFilterBoxStyle(container, group) {
      const dataStyle = String(container?.dataset?.filterStyle || '').toLowerCase();
      if (dataStyle === 'box') return true;
      if (dataStyle === 'list') return false;
      return group?.layout === 'category_box';
    }

    applyFilterColumns(container, columns) {
      if (!container) return;

      const cols = Number(columns || container.dataset.filterColumns) === 2 ? 2 : 1;
      container.classList.add('filter-options--columns');
      container.style.setProperty('--filter-columns', String(cols));
      container.dataset.filterColumns = String(cols);
    }

    applyTagFilterLayouts() {
      (this.config.tagFilterGroups || []).forEach((group) => {
        const key = 'tag_' + group.id;
        const container = this.filtersEl.querySelector(`[data-nav-col-filter="${key}"]`);
        if (!container) return;

        if (group.display === 'color' || group.layout === 'swatches') {
          const cols = Number(group.columns || container.dataset.filterColumns) === 2 ? 2 : 1;
          container.style.setProperty('--color-filter-columns', String(cols));
          container.dataset.filterColumns = String(cols);
          return;
        }

        const isBox = this.isFilterBoxStyle(container, group);

        container.classList.toggle('tag-box-filter-options', isBox);
        container.classList.toggle('filter-options--list', !isBox);
        container.querySelectorAll('.filter-option').forEach((option) => {
          option.classList.toggle('filter-option--tag-box', isBox);
        });
        this.applyFilterColumns(container, group.columns);
      });
      this.syncTagBoxState();
    }

    applyProductTypeFilterLayout() {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="product_type"]');
      if (!container) return;

      const useBox = this.isProductTypeBoxStyle();
      container.classList.toggle('tag-box-filter-options', useBox);
      container.classList.toggle('filter-options--list', !useBox);
      container.querySelectorAll('.filter-option').forEach((option) => {
        option.classList.toggle('filter-option--tag-box', useBox);
      });
      const columns = this.config?.productTypeFilter?.columns;
      this.applyFilterColumns(container, columns);
      this.syncTagBoxState();
    }

    syncTagBoxes() {
      (this.config.tagFilterGroups || []).forEach((group) => {
        const key = 'tag_' + group.id;
        const container = this.filtersEl.querySelector(`[data-nav-col-filter="${key}"]`);
        if (!container) return;
        const pool = this.getPoolExcluding(key);

        if (group.display === 'color' || group.layout === 'swatches') {
          container.querySelectorAll('.filter-checkbox').forEach((checkbox) => {
            const handles = String(checkbox.value || '')
              .split('|')
              .map(normalizeColorKey)
              .filter(Boolean);
            let count = 0;
            if (handles.length) {
              for (let i = 0; i < pool.length; i += 1) {
                if (handles.some((c) => pool[i].colors.includes(c))) count += 1;
              }
            }
            const option = checkbox.closest('.filter-option');
            const base =
              option?.querySelector('.filter-label')?.dataset.filterLabelBase ||
              formatLabel(handles[0] || '');
            this.updateFilterOptionCount(option, checkbox, count, base);
          });
          return;
        }

        container.querySelectorAll('.filter-checkbox').forEach((checkbox) => {
          const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === key);
          const tagPrefix = group && group.tagPrefix ? group.tagPrefix : '';
          const matchTags = resolveTagFilterHandles(
            expandTagFilterValue(checkbox.value),
            group
          );
          let count = 0;
          if (matchTags.length) {
            for (let i = 0; i < pool.length; i += 1) {
              if (productMatchesTagHandles(pool[i], matchTags, tagPrefix)) count += 1;
            }
          }
          const option = checkbox.closest('.filter-option');
          const base =
            option?.querySelector('.filter-label')?.dataset.filterLabelBase ||
            formatLabel(matchTags[0] || checkbox.value || '');
          this.updateFilterOptionCount(option, checkbox, count, base);
        });
      });
    }

    updateCheckboxFacetCounts(name, countFn) {
      const container = this.filtersEl.querySelector(`[data-nav-col-filter="${name}"]`);
      if (!container) return;

      container.querySelectorAll('.filter-checkbox').forEach((checkbox) => {
        const count = countFn(checkbox.value);
        const option = checkbox.closest('.filter-option');
        const base =
          option?.querySelector('.filter-label')?.dataset.filterLabelBase ||
          formatLabel(checkbox.value);
        this.updateFilterOptionCount(option, checkbox, count, base);
      });
    }

    applyFacetCountsToContainer(name, countMap) {
      const container = this.filtersEl.querySelector(`[data-nav-col-filter="${name}"]`);
      if (!container) return;

      container.querySelectorAll('.filter-checkbox').forEach((checkbox) => {
        const key = handleize(checkbox.value);
        const count = countMap.get(key) || 0;
        const option = checkbox.closest('.filter-option');
        const base =
          option?.querySelector('.filter-label')?.dataset.filterLabelBase ||
          formatLabel(checkbox.value);
        this.updateFilterOptionCount(option, checkbox, count, base);
      });
    }

    syncStandardFacetCounts() {
      this.clearFacetPoolCache();

      const ptPool = this.getPoolExcluding('product_type');
      const ptCounts = new Map();
      for (let i = 0; i < ptPool.length; i += 1) {
        const type = ptPool[i].type;
        if (type) ptCounts.set(type, (ptCounts.get(type) || 0) + 1);
      }
      this.applyFacetCountsToContainer('product_type', ptCounts);

      const sizePool = this.getPoolExcluding('size');
      this.syncSizeFacetCounts(sizePool);

      const vendorPool = this.getPoolExcluding('vendor');
      const vendorCounts = new Map();
      for (let i = 0; i < vendorPool.length; i += 1) {
        const vendor = vendorPool[i].vendor;
        if (vendor) vendorCounts.set(vendor, (vendorCounts.get(vendor) || 0) + 1);
      }
      this.applyFacetCountsToContainer('vendor', vendorCounts);

      const colorPool = this.getPoolExcluding('color');
      this.filtersEl.querySelectorAll('[data-nav-col-filter="color"] .filter-checkbox').forEach((checkbox) => {
        const wanted = String(checkbox.value)
          .split('|')
          .map(normalizeColorKey)
          .filter(Boolean);
        let count = 0;
        if (wanted.length) {
          for (let i = 0; i < colorPool.length; i += 1) {
            if (wanted.some((c) => colorPool[i].colors.includes(c))) count += 1;
          }
        }
        const option = checkbox.closest('.filter-option');
        const base =
          option?.querySelector('.filter-label')?.dataset.filterLabelBase ||
          formatLabel(checkbox.value);
        this.updateFilterOptionCount(option, checkbox, count, base);
      });

      const availPool = this.getPoolExcluding('availability');
      const availCounts = { 'in-stock': 0, 'out-of-stock': 0 };
      for (let i = 0; i < availPool.length; i += 1) {
        availCounts[availPool[i].availability] += 1;
      }
      this.filtersEl.querySelectorAll('input[name="availability"]').forEach((input) => {
        const count = availCounts[input.value] || 0;
        const option = input.closest('.filter-option');
        const base = input.value === 'in-stock' ? 'In Stock' : 'Out of Stock';
        this.updateFilterOptionCount(option, input, count, base);
      });
    }

    syncSizeFacetCounts(pool) {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="size"]');
      if (!container) return;

      container.querySelectorAll('.filter-checkbox').forEach((checkbox) => {
        const handles = String(checkbox.value || '')
          .split('|')
          .map(handleize)
          .filter(Boolean);
        let count = 0;
        if (handles.length) {
          for (let i = 0; i < pool.length; i += 1) {
            const productHandles = this.getProductSizeHandles(pool[i]);
            if (handles.some((h) => productHandles.includes(h))) count += 1;
          }
        }
        const option = checkbox.closest('.filter-option');
        const base =
          option?.querySelector('.filter-label')?.dataset.filterLabelBase ||
          formatSizeLabel(checkbox.value);
        this.updateFilterOptionCount(option, checkbox, count, base);
      });
    }

    scheduleFacetSync() {
      window.clearTimeout(this.facetSyncTimer);
      this.facetSyncTimer = window.setTimeout(() => {
        this.facetSyncTimer = null;
        const run = () => {
          this.syncAllFacetCounts();
        };
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(run, { timeout: 180 });
        } else {
          window.requestAnimationFrame(run);
        }
      }, FACET_SYNC_DEBOUNCE_MS);
    }

    async warmInitialCardCache() {
      const handles = this.getOrderedHandles().slice(0, this.getBatchSize());
      const missing = handles.filter((handle) => handle && !this.cardHtmlCache.has(handle));
      if (!missing.length || !this.config) return;
      try {
        await this.fetchCardsByHandles(missing);
      } catch (error) {
        this.debug.warn('initial-card-cache-failed', { message: error.message });
      }
    }

    syncAllFacetCounts() {
      this.clearFacetPoolCache();
      this.syncTagBoxes();
      this.syncStandardFacetCounts();
      this.syncGroupVisibility();
    }

    syncGroupVisibility() {
      this.filtersEl.querySelectorAll('.filter-group').forEach((group) => {
        if (group.querySelector('.price-filter-container')) return;

        const options = [...group.querySelectorAll('.filter-option')].filter((option) =>
          this.isFilterOptionVisible(option)
        );
        const isEmpty = options.length === 0;

        group.hidden = isEmpty;
        group.classList.toggle('is-empty-filter-group', isEmpty);
        group.style.display = isEmpty ? 'none' : '';
        group.setAttribute('aria-hidden', isEmpty ? 'true' : 'false');
      });
    }

    /* ——— Chips, counts, URL ——— */

    updateChips() {
      if (!this.tagsEl) return;
      this.tagsEl.innerHTML = '';
      this._activeFiltersList = null;
      const activeForDisplay = this.active.size ? this.active : this.collectActiveFromDom();
      if (!activeForDisplay.size) {
        this.tagsEl.hidden = true;
        return;
      }
      if (!this.active.size && activeForDisplay.size) {
        this.active = activeForDisplay;
      }
      this.tagsEl.hidden = false;

      const header = document.createElement('div');
      header.className = 'nav-col-active-filters__header';
      const title = document.createElement('p');
      title.className = 'nav-col-active-filters__title';
      title.textContent = 'Refine by';
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.className = 'nav-col-active-filters__clear';
      clearBtn.textContent = 'Clear All';
      clearBtn.addEventListener('click', () => this.clearAll());
      header.appendChild(title);
      header.appendChild(clearBtn);
      this.tagsEl.appendChild(header);

      const list = document.createElement('ul');
      list.className = 'nav-col-active-filters__list';
      this.tagsEl.appendChild(list);
      this._activeFiltersList = list;

      activeForDisplay.forEach((values, key) => {
        if (key === 'price') {
          const { min, max } = values;
          let text = '';
          if (min > 0 && max < Infinity) text = `$${min} - $${max}`;
          else if (min > 0) text = `Over $${min}`;
          else if (max < Infinity) text = `Under $${max}`;
          if (text) this.appendChip(this.filterCategoryLabel(key), text, key, null);
          return;
        }
        Array.from(values).forEach((value) => {
          this.appendChip(this.filterCategoryLabel(key), this.filterValueLabel(key, value), key, value);
        });
      });
    }

    filterCategoryLabel(key) {
      if (key === 'vendor') return 'Brand';
      if (key === 'size') return 'Size';
      if (key === 'color') return 'Color';
      if (key === 'availability') return 'Availability';
      if (key === 'product_type') return this.config?.productTypeFilter?.title || 'Product Type';
      if (key === 'price') return 'Price';
      const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === key);
      return (group && group.title) || 'Filter';
    }

    filterValueLabel(key, value) {
      if (key === 'color') return this.colorFilterLabel(value);
      if (key === 'size') return this.sizeFilterLabel(value);
      if (key === 'availability') {
        if (value === 'in-stock') return 'In Stock';
        if (value === 'out-of-stock') return 'Out of Stock';
      }

      const input = this.filtersEl.querySelector(
        `input[name="${escapeSelector(key)}"][value="${escapeSelector(value)}"]`
      );
      const inputLabel = input?.closest('.filter-option')?.querySelector('.filter-label');
      if (inputLabel?.dataset.filterLabelBase) {
        return inputLabel.dataset.filterLabelBase.trim();
      }
      if (inputLabel?.textContent) {
        return inputLabel.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }

      const group = (this.config.tagFilterGroups || []).find((g) => 'tag_' + g.id === key);
      if (group && (group.display === 'color' || group.layout === 'swatches')) {
        const colorGroup = (group.colorGroups || []).find((item) => item.value === value);
        if (colorGroup?.label) return String(colorGroup.label).trim();
      }

      return formatLabel(value);
    }

    appendChip(categoryLabel, valueLabel, key, filterValue) {
      const list = this._activeFiltersList;
      if (!list) return;

      const item = document.createElement('li');
      item.className = 'nav-col-active-filters__item';

      const text = document.createElement('span');
      text.className = 'nav-col-active-filters__text';

      const category = document.createElement('span');
      category.className = 'nav-col-active-filters__category';
      category.textContent = `${categoryLabel}: `;

      const value = document.createElement('strong');
      value.className = 'nav-col-active-filters__value';
      value.textContent = valueLabel;

      text.appendChild(category);
      text.appendChild(value);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'nav-col-active-filters__remove';
      removeBtn.setAttribute('aria-label', `Remove ${categoryLabel}: ${valueLabel}`);
      removeBtn.addEventListener('click', () => {
        if (filterValue != null) {
          const input = this.filtersEl.querySelector(
            `input[name="${escapeSelector(key)}"][value="${escapeSelector(filterValue)}"]`
          );
          if (input) input.checked = false;
        } else {
          this.active.delete(key);
          const minEl = this.filtersEl.querySelector('input[name="price_min"]');
          const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
          if (minEl) minEl.value = '';
          if (maxEl) maxEl.value = '';
        }
        this.scheduleApply({ resetPage: true });
      });

      item.appendChild(text);
      item.appendChild(removeBtn);
      list.appendChild(item);
    }

    sizeFilterLabel(value) {
      const raw = String(value || '');
      const input = this.filtersEl.querySelector(
        `input[name="size"][value="${escapeSelector(raw)}"]`
      );
      const inputLabel = input?.closest('.filter-option')?.querySelector('.filter-label');
      if (inputLabel?.dataset.filterLabelBase) {
        return inputLabel.dataset.filterLabelBase.trim();
      }
      const sizeGroup = ((this.config && this.config.sizeGroups) || []).find((item) => item.value === raw);
      if (sizeGroup?.label) {
        return String(sizeGroup.label).trim();
      }
      if (inputLabel?.textContent) {
        return inputLabel.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }
      const firstHandle = raw.split('|').map((part) => part.trim()).filter(Boolean)[0];
      return formatSizeLabel(firstHandle || raw);
    }

    colorFilterLabel(value) {
      const raw = String(value || '');
      const input = this.filtersEl.querySelector(
        `input[name="color"][value="${escapeSelector(raw)}"]`
      );
      const inputLabel = input?.closest('.filter-option')?.querySelector('.filter-label');
      if (inputLabel?.dataset.filterLabelBase) {
        return inputLabel.dataset.filterLabelBase.trim();
      }
      const colorGroup = (this.config && this.config.colorGroups || []).find((item) => item.value === raw);
      if (colorGroup?.label) {
        return String(colorGroup.label).trim();
      }
      if (inputLabel?.textContent) {
        return inputLabel.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }
      const firstHandle = raw.split('|').map((part) => part.trim()).filter(Boolean)[0];
      return formatLabel(firstHandle || raw);
    }

    isProductTypeBoxStyle() {
      const container = this.filtersEl.querySelector('[data-nav-col-filter="product_type"]');
      const dataStyle = String(container?.dataset.filterStyle || '').toLowerCase();
      if (dataStyle === 'box') return true;
      if (dataStyle === 'list') return false;
      return this.config?.productTypeFilter?.layout === 'category_box';
    }

    updateFilterCount() {
      const el =
        this.section.querySelector('#nav-col-active-filter-count') ||
        this.section.querySelector('.nav-col-active-filter-count');
      if (!el) return;

      let n = 0;
      this.active.forEach((v) => {
        n += v instanceof Set ? v.size : 1;
      });

      if (n > 0) {
        el.textContent = '(' + n + ')';
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    }

    updateResultsCount(count) {
      if (!this.resultsEl) return;
      const n = typeof count === 'number' ? count : this.products.length;
      this.resultsEl.textContent = n + (n === 1 ? ' product' : ' products');
    }

    updateNoResults(count) {
      if (!this.noResultsEl) return;
      if (count === 0) {
        this.noResultsEl.style.display = 'block';
      } else {
        this.noResultsEl.style.display = 'none';
      }
    }

    getPage() {
      const p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    }

    buildUrlParams(clearFilters) {
      const params = new URLSearchParams();
      mergeSearchContextParams(params, this.config);
      const current = new URLSearchParams(window.location.search);
      const sort = current.get('sort_by');
      if (sort) params.set('sort_by', sort);
      if (!clearFilters) {
        this.active.forEach((values, key) => {
          if (key === 'price') {
            if (values.min > 0) params.set('price_min', values.min);
            if (values.max < Infinity) params.set('price_max', values.max);
          } else {
            params.set(key, Array.from(values).join(','));
          }
        });
      }
      return mergeEditorSearchParams(params);
    }

    updateUrl(_page, clearFilters) {
      const params = this.buildUrlParams(clearFilters);
      const q = params.toString();
      const path = window.location.pathname;
      window.history.replaceState({}, '', q ? path + '?' + q : path);
    }

    loadFromUrl() {
      const params = new URLSearchParams(window.location.search);
      if (isThemeEditor()) {
        // Theme editor previewPath can carry stale filter params from prior sessions.
        // Always start unfiltered in editor so products are never hidden by persisted state.
        this.active.clear();
        this.clearUrlFilterPendingState();
        void this.restore();
        return;
      }

      const fromUrl = parseActiveFiltersFromUrlParams(params, this.config);
      if (fromUrl.size > 0) {
        this._urlFiltersPending = true;
        this.prepareGridForUrlFilters();
        this.active = fromUrl;
        this.debug.log('load-from-url', {
          params: Object.fromEntries([...params.entries()].filter(([k]) => k !== 'sort_by'))
        });
        this.applyActiveFiltersToDom();
        void this.tryApplyUrlFilters();
        return;
      }

      let has = false;
      this.filtersEl.querySelectorAll('.filter-checkbox').forEach((input) => {
        const vals = (params.get(input.name) || '').split(',').filter(Boolean);
        if (vals.includes(input.value)) {
          input.checked = true;
          has = true;
        }
      });
      const min = params.get('price_min');
      const max = params.get('price_max');
      if (min) {
        const el = this.filtersEl.querySelector('input[name="price_min"]');
        if (el) el.value = min;
        has = true;
      }
      if (max) {
        const el = this.filtersEl.querySelector('input[name="price_max"]');
        if (el) el.value = max;
        has = true;
      }
      if (has) {
        this.debug.log('load-from-url-dom', {
          params: Object.fromEntries([...params.entries()].filter(([k]) => k !== 'sort_by'))
        });
        this.readActiveFromDom();
        void this.apply({ resetPage: false });
      } else {
        this.active.clear();
        this.clearUrlFilterPendingState();
        if (!this.needsFullCatalog() || this.fullCatalogLoaded) {
          void this.restore();
        }
      }
    }

    renderPagination(total, page, totalPages) {
      const parent = this.section.querySelector('.pagination')?.parentNode;
      if (!parent) return;
      let el = this.section.querySelector('.pagination');
      if (totalPages <= 1) {
        if (el) el.remove();
        return;
      }
      const params = this.buildUrlParams(0, false);
      const path = window.location.pathname;
      let html = '';
      if (page > 1) {
        params.set('page', String(page - 1));
        html += `<a class="pagination__prev pagination__link nav-col-page-link" href="${path}?${params}">&laquo; Previous</a>`;
      }
      for (let i = 1; i <= totalPages; i++) {
        params.set('page', String(i));
        html +=
          i === page
            ? `<span class="pagination__number">${i}</span>`
            : `<a class="pagination__link nav-col-page-link" href="${path}?${params}">${i}</a>`;
      }
      if (page < totalPages) {
        params.set('page', String(page + 1));
        html += `<a class="pagination__next pagination__link nav-col-page-link" href="${path}?${params}">Next &raquo;</a>`;
      }
      const markup = `<div class="pagination nav-col-client-pagination">${html}</div>`;
      if (el) el.outerHTML = markup;
      else parent.insertAdjacentHTML('beforeend', markup);
    }

    /* ——— Events ——— */

    bindEvents() {
      const primeCatalog = () => {
        if (!isMobile()) {
          void this.ensureFullCatalogLoad();
        }
      };
      this.filtersEl.addEventListener('pointerenter', primeCatalog, { once: true });
      this.filtersEl.addEventListener('focusin', primeCatalog, { once: true });

      const onChange = (e) => {
        if (this._suppress) return;
        if (!e.target.classList.contains('filter-checkbox')) return;
        this.debug.log('filter-change', { name: e.target.name, value: e.target.value, checked: e.target.checked });
        if (isMobile()) {
          void this.ensureFullCatalogLoad();
          this.readActiveFromDom();
          this.updateFilterCount();
          this.updateChips();
          return;
        }
        this.scheduleApply({ resetPage: true });
      };
      this.filtersEl.addEventListener('change', onChange);
      this.track(() => this.filtersEl.removeEventListener('change', onChange));

      const priceBtn = this.filtersEl.querySelector('.apply-price-btn');
      if (priceBtn) {
        const onPrice = () => {
          const minEl = this.filtersEl.querySelector('input[name="price_min"]');
          const maxEl = this.filtersEl.querySelector('input[name="price_max"]');
          const min = minEl ? parseFloat(minEl.value) || 0 : 0;
          const max = maxEl ? parseFloat(maxEl.value) || Infinity : Infinity;
          if (min > 0 || max < Infinity) this.active.set('price', { min, max });
          else this.active.delete('price');
          if (isMobile()) {
            this.updateFilterCount();
            this.updateChips();
            return;
          }
          this.applyFilters({ resetPage: true });
        };
        priceBtn.addEventListener('click', onPrice);
        this.track(() => priceBtn.removeEventListener('click', onPrice));
      }

      const clearBtn = this.filtersEl.querySelector('.clear-filters-btn');
      if (clearBtn) {
        const onClear = () => this.clearAll();
        clearBtn.addEventListener('click', onClear);
        this.track(() => clearBtn.removeEventListener('click', onClear));
      }

      const clearLink = this.section.querySelector('.clear-all-filters-link');
      if (clearLink) {
        const onClearLink = () => this.clearAll();
        clearLink.addEventListener('click', onClearLink);
        this.track(() => clearLink.removeEventListener('click', onClearLink));
      }
    }
  }

  NavColFilters.collectFilterParamKeys = collectFilterParamKeys;
  NavColFilters.mergeEditorSearchParams = mergeEditorSearchParams;
  NavColFilters.urlHasActiveFilterParams = urlHasActiveFilterParams;
  NavColFilters.parseActiveFiltersFromUrlParams = parseActiveFiltersFromUrlParams;

  window.NavColFilters = NavColFilters;
})();
